/**
 * Ad schedule and ad budget (PRD §4, C18). One pre-roll per clip, at most 3 video ads per
 * page view, and from pre-roll 2 on, ad time never exceeds content actually played.
 *
 * Assumption A17: the budget is page-wide (shared by every player), because §4 defines it
 * per page view. The 80% rule applies to the previous clip in the same player's playlist.
 */

import { qaShortClips } from './qa.js';

/**
 * Create the page-wide ad budget.
 * @param {object} schedule config.adSchedule (already locked)
 * @returns {object}
 */
export function createPageBudget(schedule) {
  const state = { adsServed: 0, adSecondsServed: 0, contentSecondsPlayed: 0 };
  return {
    state,
    /** @param {number} seconds actual ad seconds played */
    addAdSeconds(seconds) {
      state.adSecondsServed += Math.max(0, seconds || 0);
    },
    /** Count one served ad (IMA STARTED = the impression). */
    countAd() {
      state.adsServed += 1;
    },
    /** @param {number} seconds content seconds actually played (no seeking) */
    addContentSeconds(seconds) {
      state.contentSecondsPlayed += Math.max(0, seconds || 0);
    },
    /** @returns {number} remaining budget in ms (content played - ads served) */
    remainingMs() {
      return Math.floor((state.contentSecondsPlayed - state.adSecondsServed) * 1000);
    },
    schedule,
  };
}

/**
 * Create one player's playlist counters.
 * @returns {object}
 */
export function createPlayerCounters() {
  return { adsServed: 0, clipsStarted: 0, contentSeconds: 0, lastClipPlayedFraction: null };
}

/**
 * Decide whether the pre-roll before a clip may be requested, and with which max_ad_duration.
 * @param {object} input
 * @param {object} input.budget page budget from createPageBudget
 * @param {object} input.counters this player's counters
 * @param {{duration:number}} input.clip the clip that follows the ad
 * @param {number} input.clipIndex 0-based position in the playlist
 * @returns {{request:boolean, maxAdDurationMs:number, reason:string}}
 */
export function nextPrerollDecision({ budget, counters, clip, clipIndex }) {
  const schedule = budget.schedule;
  const no = (reason) => ({ request: false, maxAdDurationMs: 0, reason });
  if (!schedule.prerollPerClip && clipIndex > 0) return no('preroll_per_clip_off');
  // C4: never call IMA without content to follow.
  if (!clip) return no('no_clip');
  // C18: no ad before a clip under 40 s (library duration, vid_d).
  if (!(clip.duration >= schedule.minClipSecondsForAd) && !qaShortClips()) return no('clip_too_short');
  // §4 caps: clips and content per player.
  // A40: a loop replays clip 0, so count plays as well as the index.
  if (Math.max(clipIndex, counters.clipsStarted) >= schedule.maxClipsPerView) return no('clip_cap');
  if (counters.contentSeconds >= schedule.maxContentSecondsPerView) return no('content_cap');
  // C18: at most 3 video ads per page across all players, and per player.
  if (budget.state.adsServed >= schedule.maxVideoAdsPerView) return no('page_ad_cap');
  if (counters.adsServed >= schedule.maxVideoAdsPerView) return no('player_ad_cap');
  // Pre-roll 1 of the page view: capped at 30 s.
  if (budget.state.adsServed === 0) return { request: true, maxAdDurationMs: 30000, reason: 'preroll_1' };
  // Later pre-rolls: (a) 80% of the previous clip in this playlist actually played.
  if (counters.lastClipPlayedFraction !== null && counters.lastClipPlayedFraction < schedule.minPrevClipPlayed) {
    return no('prev_clip_under_80');
  }
  // (b) remaining budget >= 15 s; request max_ad_duration = budget, capped at 30 s.
  const remaining = budget.remainingMs();
  if (remaining < schedule.minAdBudgetMs) return no('budget_below_15s');
  const maxAdDurationMs = Math.min(30000, Math.floor(remaining / 1000) * 1000);
  return { request: true, maxAdDurationMs, reason: 'budget_ok' };
}

/**
 * Track seconds actually played from timeupdate samples. Seeking never counts (§4).
 * @returns {{sample:Function, reset:Function, seconds:Function, markSeek:Function}}
 */
export function createPlayedTracker() {
  let last = null;
  let total = 0;
  return {
    /**
     * @param {number} currentTime video.currentTime
     * @param {boolean} playing whether the media is playing (not paused, not seeking)
     * @returns {number} seconds added by this sample
     */
    sample(currentTime, playing) {
      let added = 0;
      if (playing && last !== null) {
        const delta = currentTime - last;
        // timeupdate fires every ~250 ms; anything larger or negative is a seek.
        if (delta > 0 && delta <= 1.5) {
          added = delta;
          total += delta;
        }
      }
      last = currentTime;
      return added;
    },
    markSeek(currentTime) {
      last = currentTime;
    },
    reset() {
      last = null;
      total = 0;
    },
    seconds() {
      return total;
    },
  };
}
